import type { RecoveryAuditDecision } from '@/lib/api/schemas/product-recovery.schema'
import { relationLabel, storedBasisLabels, storedOutcomeLabels } from '@/lib/recovery/labels'
import { summariseMovement } from '@/lib/recovery/movement'

/**
 * What a decision moved, per relation, in words.
 *
 * The stored evidence is a JSON object and never reaches the operator as one. A
 * targetless finding says plainly that nothing moved, rather than rendering an
 * empty list that reads like a failure to load.
 */
function Movement({ decision }: { decision: RecoveryAuditDecision }) {
  const summary = summariseMovement(decision.outcome, decision.movementEvidence)

  if (summary.kind === 'deleted') {
    return (
      <p className="mt-1 text-[11px] text-ink-muted" data-movement="deleted">
        Запись удалена: у неё не было ни связей, ни кандидата. Ничего не переносилось.
      </p>
    )
  }
  if (summary.kind === 'nothing_moved') {
    return (
      <p className="mt-1 text-[11px] text-ink-muted" data-movement="nothing_moved">
        Ничего не перенесено: изделие не выбиралось.
      </p>
    )
  }
  if (summary.kind === 'distributed') {
    return (
      <div className="mt-1 text-[11px] text-ink-muted" data-movement="distributed">
        {summary.targets.length === 0 ? (
          <p data-movement-empty="true">Связей для распределения не было.</p>
        ) : (
          <>
            <p>Распределено по изделиям:</p>
            <ul>
              {summary.targets.map((target) => (
                <li key={target.productId} data-distributed-product={target.productId} data-distributed-rows={target.rows}>
                  Изделие {target.productId}: {target.rows.toLocaleString('ru-RU')}
                </li>
              ))}
            </ul>
          </>
        )}
        {summary.retained.length === 0 ? null : (
          <>
            <p className="mt-1">Осталось на исходной записи:</p>
            <ul>
              {summary.retained.map((each) => (
                <li key={each.relation} data-retained-relation={each.relation} data-retained-rows={each.rows}>
                  {relationLabel(each.relation)}: {each.rows.toLocaleString('ru-RU')}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    )
  }
  return (
    <div className="mt-1 text-[11px] text-ink-muted" data-movement="moved">
      {summary.moved.length === 0 ? (
        <p data-movement-empty="true">Связей для переноса не было.</p>
      ) : (
        <>
          <p>Перенесено на выбранное изделие:</p>
          <ul>
            {summary.moved.map((each) => (
              <li key={each.relation} data-moved-relation={each.relation} data-moved-rows={each.rows}>
                {relationLabel(each.relation)}: {each.rows.toLocaleString('ru-RU')}
              </li>
            ))}
          </ul>
        </>
      )}
      {summary.retained.length === 0 ? null : (
        <>
          <p className="mt-1">Осталось на исходной записи:</p>
          <ul>
            {summary.retained.map((each) => (
              <li key={each.relation} data-retained-relation={each.relation} data-retained-rows={each.rows}>
                {relationLabel(each.relation)}: {each.rows.toLocaleString('ru-RU')}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

/**
 * Every decision ever recorded for this subject, a machine's beside a person's.
 *
 * The screen is told only that a settled subject is `closed`, so this is the one
 * place the actual outcome and its basis are readable. That is why it sits on the
 * panel rather than behind a control: a recognition nobody can find again is a
 * recognition that was not really recorded.
 */
export function DecisionHistory({ items }: { items: readonly RecoveryAuditDecision[] }) {
  return (
    <section className="mt-4" data-testid="recovery-history">
      <h3 className="text-sm font-semibold text-ink">История решений</h3>
      {items.length === 0 ? (
        <p className="mt-1 text-xs text-ink-muted" data-history-empty="true">
          По этой записи решений пока нет.
        </p>
      ) : (
        <ul className="mt-2 space-y-2">
          {items.map((decision) => (
            <li
              key={decision.decisionId}
              className="rounded-md border border-rule bg-paper p-2 text-xs text-ink-2"
              data-decision-id={decision.decisionId}
              data-stored-outcome={decision.outcome}
              data-stored-basis={decision.basis}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium text-ink">{storedOutcomeLabels[decision.outcome]}</span>
                <span className="text-[11px] text-ink-muted">
                  {new Date(decision.decidedAt).toLocaleString('ru-RU')}
                </span>
              </div>
              <p className="mt-0.5">Основание: {storedBasisLabels[decision.basis]}</p>
              <p className="text-[11px] text-ink-muted">
                {decision.decidedBy} · версия записи {decision.expectedRevision.toLocaleString('ru-RU')}
              </p>
              {decision.evidenceNote === '' ? null : <p className="mt-0.5">{decision.evidenceNote}</p>}
              <Movement decision={decision} />
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
